import { useState, useRef, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { 
  ChatBubbleLeftRightIcon, 
  XMarkIcon, 
  PaperAirplaneIcon,
  MinusIcon,
  ShieldCheckIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import useAppStore from '../../stores/useAppStore';
import { chatApi } from '../../services/api/chatApi';
import { handleApiError } from '../../services/api/errorHandler';
import { siteContent } from '../../data/content';
import ChatStatusIndicator from './ChatStatusIndicator';
import ChatCaptcha from './ChatCaptcha';
import { useNavigate, useLocation } from 'react-router-dom';
import { uiLogger } from '../../utils/logger';
import { useBotDetection } from '../../hooks/useBotDetection';
import { rateLimitService } from '../../services/api/rateLimitService';
import { messageValidationService } from '../../services/api/messageValidationService';

export default function ChatWidget(): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    isChatOpen,
    isChatMinimized,
    chatMessages,
    chatSessionId,
    isTyping,
    chatStatus,
    unreadCount,
    openChat,
    closeChat,
    toggleMinimizeChat,
    addMessage,
    setChatSessionId,
    setIsTyping,
    clearChat,
    markMessagesAsRead,
    getChatStatusInfo
  } = useAppStore();

  const [inputMessage, setInputMessage] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Protection features state
  const [showCaptcha, setShowCaptcha] = useState<boolean>(false);
  const [captchaVerified, setCaptchaVerified] = useState<boolean>(false);
  const [isBlocked, setIsBlocked] = useState<boolean>(false);
  const [blockReason, setBlockReason] = useState<string>('');
  const [messageCount, setMessageCount] = useState<number>(0);
  const [pendingMessage, setPendingMessage] = useState<string>('');
  const [shouldExecuteCaptcha, setShouldExecuteCaptcha] = useState<boolean>(false);

  // Bot detection
  const botDetection = useBotDetection();
  
  // reCAPTCHA configuration
  const isInvisibleCaptcha = import.meta.env.VITE_RECAPTCHA_INVISIBLE === 'true';

  const { chat } = siteContent;

  // Bot detection effect - only block if very high confidence
  useEffect(() => {
    if (botDetection.isBot && botDetection.confidence > 0.8) {
      uiLogger.warn('High-confidence bot detected:', botDetection.reasons);
      setIsBlocked(true);
      setBlockReason('Automated access detected. Please contact support if you believe this is an error.');
    }
  }, [botDetection]);

  // Function to check if device is mobile
  const isMobile = (): boolean => {
    return window.innerWidth < 768; // Common breakpoint for mobile
  };

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages, isTyping]);

  // Focus input when chat opens or is restored from minimize
  useEffect(() => {
    if (isChatOpen && !isChatMinimized) {
      setTimeout(() => inputRef.current?.focus(), 100);
      // Mark messages as read when chat becomes visible
      if (unreadCount > 0) {
        markMessagesAsRead();
      }
    }
  }, [isChatOpen, isChatMinimized, unreadCount, markMessagesAsRead]);

  // Send welcome message when chat first opens
  useEffect(() => {
    if (isChatOpen && chatMessages.length === 0) {
      addMessage({
        role: 'assistant',
        content: [{ text: chat.welcome }],
        isWelcome: true
      });
    }
  }, [isChatOpen, chatMessages.length, addMessage, chat.welcome]);

  const handleSendMessage = async (): Promise<void> => {
    if (!inputMessage.trim()) return;

    // Check if blocked
    if (isBlocked) {
      setError(blockReason);
      return;
    }

    const userMessage = inputMessage.trim();

    // Validate message
    const validation = messageValidationService.validateMessage(userMessage);
    if (!validation.isValid) {
      setError(validation.reason || 'Invalid message');
      return;
    }

    // Check rate limits
    const rateLimitCheck = rateLimitService.checkLimit(chatSessionId || 'default');
    if (!rateLimitCheck.allowed) {
      setError(rateLimitCheck.reason || 'Rate limit exceeded');
      return;
    }

    // Check if CAPTCHA is needed (primarily based on message count and suspicious content)
    const needsCaptcha = (
      messageCount >= 3 || 
      messageValidationService.isSuspicious(userMessage)
      // Removed bot detection from CAPTCHA triggering since reCAPTCHA handles this better
    ) && !captchaVerified;

    if (needsCaptcha) {
      const sanitizedMessage = validation.sanitizedMessage || userMessage;
      setPendingMessage(sanitizedMessage);
      
      if (isInvisibleCaptcha) {
        // For invisible reCAPTCHA, trigger execution
        setShouldExecuteCaptcha(true);
      } else {
        // For visible reCAPTCHA, show modal
        setShowCaptcha(true);
      }
      return;
    }

    // Proceed with sending message
    await sendMessage(validation.sanitizedMessage || userMessage);
  };

  const sendMessage = async (message: string): Promise<void> => {
    setInputMessage('');
    setError(null);

    // Add user message
    addMessage({
      role: 'user',
      content: [{ text: message }]
    });

    // Record message for rate limiting
    rateLimitService.recordMessage(chatSessionId || 'default');
    setMessageCount(prev => prev + 1);

    // Show typing indicator
    setIsTyping(true);

    try {
      const response = await chatApi.sendMessage(message, chatSessionId);
      
      // Update session ID if new
      if (response.sessionId && response.sessionId !== chatSessionId) {
        setChatSessionId(response.sessionId);
      }

      // Add assistant response
      addMessage({
        role: 'assistant',
        content: response.message.content
      });
    } catch (err) {
      setError(handleApiError(err));
      addMessage({
        role: 'assistant',
        content: [{ text: 'Sorry, I encountered an error. Please try again.' }],
        isError: true
      });
    } finally {
      setIsTyping(false);
    }
  };

  const handleCaptchaVerified = async (token: string): Promise<void> => {
    // In a real implementation, you'd verify the token with your backend
    uiLogger.info('CAPTCHA verified:', token);
    setCaptchaVerified(true);
    setShowCaptcha(false);
    
    // Send the pending message
    const messageToSend = pendingMessage || inputMessage.trim();
    if (messageToSend) {
      const validation = messageValidationService.validateMessage(messageToSend);
      if (validation.isValid) {
        await sendMessage(validation.sanitizedMessage || messageToSend);
      }
    }
    
    // Clear pending message
    setPendingMessage('');
  };

  const handleCaptchaExecuteComplete = (): void => {
    setShouldExecuteCaptcha(false);
  };

  const handleCaptchaClose = (): void => {
    setShowCaptcha(false);
  };

  const handleCaptchaError = (error: string): void => {
    setError(error);
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>): void => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleCloseChat = (): void => {
    clearChat(); // Clear all chat messages and session
    setError(null); // Clear any error messages
    
    // Reset protection state
    setShowCaptcha(false);
    setCaptchaVerified(false);
    setMessageCount(0);
    setPendingMessage('');
    setShouldExecuteCaptcha(false);
    
    // Reset rate limiting for this session
    if (chatSessionId) {
      rateLimitService.resetSession(chatSessionId);
    }
    
    closeChat(); // Close the chat window
  };

  const handleOpenChat = (): void => {
    // Store the current path in session storage before opening chat
    // This will help us return to the correct page later
    try {
      sessionStorage.setItem('chatPreviousPath', location.pathname);
    } catch (error) {
      uiLogger.error('Error storing previous path', error);
    }
    
    openChat();
    
    if (isMobile()) {
      // Redirect to chat page on mobile
      uiLogger.debug('Mobile device detected, redirecting to chat page');
      navigate('/chat');
    }
  };
  
  const handleRestoreChat = (): void => {
    // For minimized chat, we need to restore it
    if (isChatMinimized) {
      toggleMinimizeChat();
      
      if (isMobile()) {
        // Store the current path before navigating
        try {
          sessionStorage.setItem('chatPreviousPath', location.pathname);
        } catch (error) {
          uiLogger.error('Error storing previous path', error);
        }
        
        // On mobile, navigate to chat page when restoring
        uiLogger.debug('Mobile device detected, restoring chat and redirecting to chat page');
        navigate('/chat');
      }
    } else {
      // Normal open chat behavior
      handleOpenChat();
    }
  };

  // Don't show the chat bubble on the chat page, but only when the chat is open and not minimized
  if (location.pathname === '/chat' && isChatOpen && !isChatMinimized) {
    return null;
  }

  return (
    <>
      {/* Enhanced Chat Button with Status Indicators - Always show when minimized or chat is closed */}
      <ChatStatusIndicator
        status={chatStatus}
        unreadCount={unreadCount}
        onClick={handleRestoreChat}
        isMinimized={isChatMinimized}
        data-testid="chat-button"
      />

      {/* Chat Window - Show when open and not minimized (desktop only) */}
      {!isMobile() && (
        <Transition show={isChatOpen && !isChatMinimized}>
          <div className="fixed bottom-6 right-6 z-50 w-96 max-w-[calc(100vw-2rem)] h-[32rem] max-h-[calc(100vh-2rem)]" data-testid="chat-window">
          <Transition.Child
            enter="transition-all duration-300 ease-out"
            enterFrom="opacity-0 scale-95 translate-y-4"
            enterTo="opacity-100 scale-100 translate-y-0"
            leave="transition-all duration-200 ease-in"
            leaveFrom="opacity-100 scale-100 translate-y-0"
            leaveTo="opacity-0 scale-95 translate-y-4"
          >
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl border border-gray-200 dark:border-gray-700 flex flex-col h-full">
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 bg-primary-600 text-white rounded-t-lg">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-primary-500 rounded-full flex items-center justify-center">
                    <ChatBubbleLeftRightIcon className="h-4 w-4" />
                  </div>
                  <div>
                    <div className="flex items-center space-x-2">
                      <h3 className="font-medium text-sm">{chat.title}</h3>
                      {/* Protection indicators */}
                      {captchaVerified && (
                        <ShieldCheckIcon className="h-4 w-4 text-green-300" title="Verified human" />
                      )}
                      {isBlocked && (
                        <ExclamationTriangleIcon className="h-4 w-4 text-red-300" title="Access blocked" />
                      )}
                    </div>
                    <p className="text-xs text-primary-100">
                      {isTyping ? 'AI is typing...' : isBlocked ? 'Access restricted' : 'Online'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={toggleMinimizeChat}
                    className="p-1 hover:bg-primary-500 rounded transition-colors duration-200"
                    aria-label={chat.minimizeButton}
                    data-testid="chat-minimize"
                  >
                    <MinusIcon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={handleCloseChat}
                    className="p-1 hover:bg-primary-500 rounded transition-colors duration-200"
                    aria-label={chat.closeButton}
                    data-testid="chat-close"
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>

              {/* Messages Area */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4" data-testid="chat-messages">
                    {chatMessages.map((message) => (
                      <div
                        key={message.id}
                        className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-[80%] p-3 rounded-lg ${
                            message.role === 'user'
                              ? 'bg-primary-600 text-white'
                              : message.isError
                              ? 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200'
                              : 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100'
                          }`}
                        >
                          {message.content.map((content, index: number) => (
                            <p key={index} className="text-sm whitespace-pre-wrap">
                              {content.text}
                            </p>
                          ))}
                          <p className="text-xs opacity-70 mt-1">
                            {new Date(message.timestamp).toLocaleTimeString([], {
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </p>
                        </div>
                      </div>
                    ))}

                    {/* Typing Indicator */}
                    {isTyping && (
                      <div className="flex justify-start">
                        <div className="bg-gray-100 dark:bg-gray-700 p-3 rounded-lg">
                          <div className="flex space-x-1">
                            <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                            <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                            <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Error Message */}
                    {error && (
                      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
                        <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
                      </div>
                    )}

                    <div ref={messagesEndRef} />
                </div>

                {/* Input Area */}
                <div className="border-t border-gray-200 dark:border-gray-700 p-4">
                  {/* Rate limit info */}
                  {messageCount > 0 && (
                    <div className="mb-2 text-xs text-gray-500 dark:text-gray-400">
                      Messages sent: {messageCount} / {rateLimitService.getLimits().messagesPerSession}
                    </div>
                  )}
                  
                  <div className="flex space-x-2">
                    <div className="flex-1">
                      <textarea
                        ref={inputRef}
                        value={inputMessage}
                        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setInputMessage(e.target.value)}
                        onKeyPress={handleKeyPress}
                        placeholder={isBlocked ? 'Chat is currently blocked' : chat.placeholder}
                        disabled={isBlocked || isTyping}
                        maxLength={messageValidationService.getLimits().maxLength}
                        className="w-full resize-none border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-100 dark:disabled:bg-gray-800 disabled:cursor-not-allowed"
                        rows={1}
                        style={{ minHeight: '2.5rem', maxHeight: '6rem' }}
                        data-testid="chat-input"
                      />
                      {/* Character count */}
                      <div className="text-xs text-gray-400 mt-1 text-right">
                        {inputMessage.length} / {messageValidationService.getLimits().maxLength}
                      </div>
                    </div>
                    <button
                      onClick={handleSendMessage}
                      disabled={!inputMessage.trim() || isTyping || isBlocked}
                      className="bg-primary-600 hover:bg-primary-700 disabled:bg-gray-300 dark:disabled:bg-gray-600 text-white p-2 rounded-lg transition-colors duration-200 disabled:cursor-not-allowed"
                      aria-label={chat.sendButton}
                      data-testid="chat-send"
                    >
                      <PaperAirplaneIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
            </div>
          </Transition.Child>
        </div>
      </Transition>
      )}

      {/* CAPTCHA Modal */}
      <ChatCaptcha
        isVisible={showCaptcha}
        onVerified={handleCaptchaVerified}
        onClose={handleCaptchaClose}
        onError={handleCaptchaError}
        shouldExecute={shouldExecuteCaptcha}
        onExecuteComplete={handleCaptchaExecuteComplete}
        isChatOpen={isChatOpen && !isChatMinimized}
      />
    </>
  );
}
